// AgentMRR subscription plans

export type SubscriptionTier = "free" | "pro" | "max";
export type PublicPlanTier = SubscriptionTier;
export type BillingPeriod = "monthly" | "yearly";
export type AccountLimit = number | "unlimited";

export interface SubscriptionDefinition {
	tier: SubscriptionTier;
	name: string;
	description: string;
	priceEUR: number;
	yearlyPriceEUR: number; // annual price (total per year)
	highlighted?: boolean;
}

export type PlanBenefitItem = {
	label: string;
	value: string | boolean;
};

export type PlanBenefitSection = {
	title: string;
	items: PlanBenefitItem[];
};

export type UpsellLimitType = "teamMembers";

export interface PlanCapabilities {
	tier: SubscriptionTier;
	generationEnabled: boolean;
	maxStorageBytes: LimitValue;
	maxBrands: number;
	maxTrackedAgents: number;
	maxConnectedSocialAccounts: AccountLimit;
	maxScheduledPosts: LimitValue;
	maxTeamMembers: number;
	schedulingEnabled: boolean;
}

export const SUBSCRIPTION_DEFINITIONS: Record<
	SubscriptionTier,
	SubscriptionDefinition
> = {
	free: {
		tier: "free",
		name: "Free",
		description: "Track 1 agent with core Agent Auth access",
		priceEUR: 0,
		yearlyPriceEUR: 0,
	},
	pro: {
		tier: "pro",
		name: "Pro",
		description: "Track 3 agents with more storage and seats",
		priceEUR: 19,
		yearlyPriceEUR: 156, // €13/mo × 12 months (35% yearly discount)
	},
	max: {
		tier: "max",
		name: "Max",
		description: "Expanded tracking capacity for larger teams",
		priceEUR: 29,
		yearlyPriceEUR: 228, // €19/mo × 12 months (35% yearly discount)
		highlighted: true,
	},
};

export const PLAN_CAPABILITIES: Record<SubscriptionTier, PlanCapabilities> = {
	free: {
		tier: "free",
		generationEnabled: true,
		maxStorageBytes: 100 * 1024 * 1024,
		maxBrands: 1,
		maxTrackedAgents: 1,
		maxConnectedSocialAccounts: 3,
		maxScheduledPosts: 10,
		maxTeamMembers: 2,
		schedulingEnabled: true,
	},
	pro: {
		tier: "pro",
		generationEnabled: true,
		maxStorageBytes: 1024 * 1024 * 1024,
		maxBrands: 1,
		maxTrackedAgents: 3,
		maxConnectedSocialAccounts: 15,
		maxScheduledPosts: 100,
		maxTeamMembers: 3,
		schedulingEnabled: true,
	},
	max: {
		tier: "max",
		generationEnabled: true,
		maxStorageBytes: 10 * 1024 * 1024 * 1024,
		maxBrands: 3,
		maxTrackedAgents: 30,
		maxConnectedSocialAccounts: 150,
		maxScheduledPosts: 1000,
		maxTeamMembers: 30,
		schedulingEnabled: true,
	},
};

export function getPlanBenefits(tier: SubscriptionTier): PlanBenefitSection[] {
	const cap = PLAN_CAPABILITIES[tier];
	const trackedAgentLabel = `${cap.maxTrackedAgents} tracked agent${cap.maxTrackedAgents > 1 ? "s" : ""}`;
	const teamLabel =
		cap.maxTeamMembers >= 999
			? "Unlimited team members"
			: `${cap.maxTeamMembers} team member${cap.maxTeamMembers > 1 ? "s" : ""}`;

	return [
		{
			title: "Distribution",
			items: [
				{
					label: "tracked agents",
					value: String(cap.maxTrackedAgents),
				},
				{
					label: "automation slots",
					value: formatLimit(cap.maxScheduledPosts),
				},
				{ label: "Automation support", value: cap.schedulingEnabled },
				{ label: "Leaderboard controls", value: true },
			],
		},
		{
			title: "Generation",
			items: [
				{ label: "Content generation", value: cap.generationEnabled },
				{ label: "A/B testing", value: tier === "pro" || tier === "max" },
			],
		},
		{
			title: "Insights",
			items: [
				{ label: "Analytics & performance", value: tier !== "free" },
				{ label: "Optimization suggestions", value: tier === "max" },
			],
		},
		{
			title: "Platform",
			items: [
				{ label: "Open source core", value: true },
				{ label: "API & Agent Auth access", value: true },
				{ label: trackedAgentLabel, value: true },
				{ label: teamLabel, value: true },
				{ label: "Email support", value: true },
				{ label: "Commercial use", value: true },
			],
		},
	];
}

export function formatPrice(priceEUR: number): string {
	if (priceEUR === 0) return "Free";
	return `€${priceEUR}`;
}

export function getMonthlyPrice(
	tier: SubscriptionTier,
	billingPeriod: BillingPeriod,
): number {
	const sub = SUBSCRIPTION_DEFINITIONS[tier];
	if (billingPeriod === "yearly") {
		return Math.round(sub.yearlyPriceEUR / 12);
	}
	return sub.priceEUR;
}

export function getYearlySavingsPercent(tier: SubscriptionTier): number {
	const sub = SUBSCRIPTION_DEFINITIONS[tier];
	if (sub.priceEUR === 0) return 0;
	const monthlyTotal = sub.priceEUR * 12;
	const savings = ((monthlyTotal - sub.yearlyPriceEUR) / monthlyTotal) * 100;
	return Math.round(savings);
}

export type LimitValue = number | "unlimited";

export function isUnlimited(value: LimitValue): value is "unlimited" {
	return value === "unlimited";
}

export function withinLimit(current: number, limit: LimitValue): boolean {
	if (isUnlimited(limit)) return true;
	return current < limit;
}

export function formatLimit(limit: LimitValue): string {
	return isUnlimited(limit) ? "∞" : String(limit);
}

export function formatAccountLimit(limit: AccountLimit): string {
	return limit === "unlimited" ? "∞" : String(limit);
}

export function getPlanCapabilities(tier: SubscriptionTier): PlanCapabilities {
	return PLAN_CAPABILITIES[tier];
}

export function getPublicPlanTier(tier: SubscriptionTier): PublicPlanTier {
	return tier;
}

export function getPublicPlanName(tier: SubscriptionTier): string {
	return SUBSCRIPTION_DEFINITIONS[tier].name;
}

export function normalizePublicPlan(
	plan: string | null | undefined,
): SubscriptionTier {
	if (!plan) return "free";

	const raw = plan.toLowerCase();
	const base = raw.replace(/-yearly$/, "");

	if (base === "free") return "free";
	if (base === "creator" || base === "starter" || base === "pro") {
		return "pro";
	}
	if (base === "business" || base === "max") {
		return "max";
	}

	return "free";
}

export interface SubscriptionRecordLike {
	plan?: string | null;
	status?: string | null;
	periodEnd?: string | Date | null;
}

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(["active"]);
const TIER_PRIORITY: Record<SubscriptionTier, number> = {
	free: 0,
	pro: 1,
	max: 2,
};

function getPeriodEndTimestamp(
	periodEnd: string | Date | null | undefined,
): number {
	if (!periodEnd) return 0;
	const date = periodEnd instanceof Date ? periodEnd : new Date(periodEnd);
	const timestamp = date.getTime();
	return Number.isFinite(timestamp) ? timestamp : 0;
}

export function isSubscriptionActiveStatus(
	status: string | null | undefined,
): boolean {
	return !!status && ACTIVE_SUBSCRIPTION_STATUSES.has(status);
}

export function selectBestActiveSubscription<T extends SubscriptionRecordLike>(
	subscriptions: T[] | null | undefined,
): T | undefined {
	return [...(subscriptions ?? [])]
		.filter((sub) => isSubscriptionActiveStatus(sub.status))
		.sort((a, b) => {
			const tierA = normalizeSubscriptionPlan(a.plan).tier;
			const tierB = normalizeSubscriptionPlan(b.plan).tier;
			const paidDiff = Number(tierB !== "free") - Number(tierA !== "free");
			if (paidDiff !== 0) return paidDiff;

			const periodDiff =
				getPeriodEndTimestamp(b.periodEnd) - getPeriodEndTimestamp(a.periodEnd);
			if (periodDiff !== 0) return periodDiff;

			return TIER_PRIORITY[tierB] - TIER_PRIORITY[tierA];
		})[0];
}

export function normalizeSubscriptionPlan(plan: string | null | undefined): {
	tier: SubscriptionTier;
	billingPeriod: BillingPeriod;
} {
	if (!plan) return { tier: "free", billingPeriod: "monthly" };

	const raw = plan.toLowerCase();
	const billingPeriod: BillingPeriod = raw.endsWith("-yearly")
		? "yearly"
		: "monthly";
	const base = raw.replace(/-yearly$/, "");

	if (base === "pro") {
		return { tier: base, billingPeriod };
	}
	if (base === "starter" || base === "creator") {
		return { tier: "pro", billingPeriod };
	}
	if (base === "max" || base === "business") {
		return { tier: "max", billingPeriod };
	}

	return { tier: "free", billingPeriod: "monthly" };
}

export function getHigherTiers(
	currentTier: SubscriptionTier,
): Exclude<SubscriptionTier, "free">[] {
	const TIER_ORDER: SubscriptionTier[] = ["free", "pro", "max"];
	const currentIndex = TIER_ORDER.indexOf(currentTier);
	return TIER_ORDER.slice(currentIndex + 1).filter(
		(t): t is Exclude<SubscriptionTier, "free"> => t !== "free",
	);
}
